import type { ErrorLike } from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { ErrorCode } from "@shuttlerock/api-types/federated";
import { i18n } from "../i18n";

type GraphQLResponseError = {
  response?: {
    errors?: Array<{
      extensions?: {
        code?: ErrorCode | string;
      } | null;
    } | null>;
  };
};

/**
 * Extracts GraphQL error codes from Apollo and graphql-request-style errors.
 * Centralizes the error code extraction logic used by multiple utilities
 */
export function extractErrorCodes(error: unknown): (ErrorCode | string)[] {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors
      .map((error) => error.extensions?.code as ErrorCode | string)
      .filter(Boolean);
  }

  if (typeof error !== "object" || error === null) {
    return [];
  }

  const graphQLResponseError = error as GraphQLResponseError;

  if (!Array.isArray(graphQLResponseError.response?.errors)) {
    return [];
  }

  return graphQLResponseError.response.errors
    .map((responseError) => responseError?.extensions?.code)
    .filter((code): code is ErrorCode | string => Boolean(code));
}

/**
 * Checks whether an unknown error is a GraphQL response error with a specific code.
 */
export function hasApolloErrorCode(error: unknown, code: ErrorCode | string): boolean {
  return extractErrorCodes(error).includes(code);
}

const DEFAULT_FALLBACK_MESSAGE_KEY = "errorHandling|generic:fallback";

/**
 * Default mapping of ErrorCode to organized i18n keys
 */
const DEFAULT_ERROR_CODE_MAP: Record<ErrorCode, string> = {
  [ErrorCode.Archived]: "errorHandling|generic:archived",
  [ErrorCode.NotFound]: "errorHandling|generic:notFound",
  [ErrorCode.Unauthorised]: "errorHandling|generic:unauthorised",
  [ErrorCode.NotAuthorised]: "errorHandling|generic:unauthorised",
  [ErrorCode.Validation]: "errorHandling|generic:validation",
  [ErrorCode.AlreadyProcessed]: DEFAULT_FALLBACK_MESSAGE_KEY,
  [ErrorCode.DimensionRatioNotUnique]: DEFAULT_FALLBACK_MESSAGE_KEY,
  [ErrorCode.Duplicate]: DEFAULT_FALLBACK_MESSAGE_KEY,
  [ErrorCode.InvalidArgument]: DEFAULT_FALLBACK_MESSAGE_KEY,
  [ErrorCode.InvalidState]: DEFAULT_FALLBACK_MESSAGE_KEY,
  [ErrorCode.Unhandled]: DEFAULT_FALLBACK_MESSAGE_KEY,
  [ErrorCode.Unknown]: DEFAULT_FALLBACK_MESSAGE_KEY,
  [ErrorCode.UserNotMatch]: DEFAULT_FALLBACK_MESSAGE_KEY,
};

/**
 * Configuration for the error mapper
 */
export interface ErrorMapperConfig {
  /**
   * Custom i18n instance to use for translation
   * If not provided, uses the error-handling package's i18n instance
   */
  customI18n?: { t: (key: string) => string };

  /**
   * Custom error code mapping that overrides defaults
   * Can use any i18n key from any namespace
   * A custom i18n instance must be provided to use this.
   */
  customErrorMap?: Partial<Record<ErrorCode | string, string>>;

  /**
   * Custom fallback message key when no mapping is found
   * If not provided, uses the default fallback
   * A custom i18n instance must be provided to use this.
   */
  customFallbackMessageKey?: string;
}

/**
 * Maps a single GraphQL error code to a user-friendly translated message
 *
 * @internal
 */
export function mapErrorCodeToMessage(
  errorCode: ErrorCode | string,
  config: ErrorMapperConfig = {},
): string {
  const { customErrorMap = {}, customI18n, customFallbackMessageKey } = config;

  // Check for custom error mapping first
  if (customI18n && errorCode in customErrorMap) {
    const errorKey = customErrorMap[errorCode];
    return customI18n.t(errorKey as string);
  }

  // Then check default mapping using default i18n
  if (errorCode in DEFAULT_ERROR_CODE_MAP) {
    const errorKey = DEFAULT_ERROR_CODE_MAP[errorCode as ErrorCode];
    return i18n.t(errorKey);
  }

  // If no mapping found, use custom fallback if provided
  if (customI18n && customFallbackMessageKey) {
    return customI18n.t(customFallbackMessageKey);
  }

  // Fallback to generic error message using custom i18n
  return i18n.t(DEFAULT_FALLBACK_MESSAGE_KEY);
}

/**
 * Maps any error (including Apollo errors) to user-friendly translated messages
 * Automatically detects Apollo errors and extracts GraphQL error codes
 * Returns an array of error messages, with fallback for non-Apollo errors
 */
export function mapApolloErrors(
  error: ErrorLike | unknown,
  config: ErrorMapperConfig = {},
): string[] | undefined {
  // Check if error is defined
  if (!error) return undefined;

  const getFallbackMessage = () => {
    if (config.customI18n && config.customFallbackMessageKey) {
      return config.customI18n.t(config.customFallbackMessageKey);
    }

    return i18n.t(DEFAULT_FALLBACK_MESSAGE_KEY);
  };

  const messages: string[] = [];

  if (CombinedGraphQLErrors.is(error)) {
    const errorCodes = extractErrorCodes(error);

    errorCodes.forEach((errorCode) => {
      const message = mapErrorCodeToMessage(errorCode, config);
      if (!messages.includes(message)) {
        messages.push(message);
      }
    });
  }

  // If no specific error messages were found, add fallback
  if (messages.length === 0) {
    messages.push(getFallbackMessage());
  }

  return messages;
}
